import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { DevModeProvider } from "./contexts/DevModeContext";
import Login from "./components/Login";
import Layout from "./components/Layout";
import Dashboard from "./components/Dashboard";
import MyProjects from "./components/MyProjects";
import AllProjects from "./components/AllProjects";
import MyTasks from "./components/MyTasks";
import AirportsAdmin from "./components/AirportsAdmin";
import PMView from "./components/PMView";
import ChartTest from "./components/ChartTest";
import SimpleCharts from "./components/SimpleCharts";
import MyHours from "./components/MyHours";
import SupplierLogin from "./components/SupplierLogin";
import QCIncidents from "./components/QCIncidents";
import QCIncidentForm from "./components/QCIncidentForm";
import QCIncidentDetail from "./components/QCIncidentDetail";
import GlobalSettings from "./components/GlobalSettings";
import FinanceAdmin from "./components/FinanceAdmin";
import BusinessDevAdmin from "./components/BusinessDevAdmin";
import PMODashboard from "./components/PMODashboard";
import Settings from "./components/Settings";
import DataMigration from "./components/DataMigration";
import UserProfile from "./components/UserProfile";
import CentralDashboard from "./components/CentralDashboard";

// Helper function to check if user is a supplier
const isSupplier = (user) => {
  if (!user) return false;
  // Check both role field and roles array
  const role = user.role?.toLowerCase();
  const roles = user.roles || [];
  return role === 'supplier' || roles.some(r => 
    typeof r === 'string' && r.toLowerCase().includes('supplier')
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowSuppliers = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect suppliers to QC incidents if they try to access non-supplier pages
  if (isSupplier(user) && !allowSuppliers) {
    return <Navigate to="/qc-incidents" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    // Redirect suppliers to QC incidents, others to dashboard
    if (isSupplier(user)) {
      return <Navigate to="/qc-incidents" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Placeholder components for routes
const AllTasks = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <h1 className="text-2xl font-bold text-gray-900 mb-4">All Tasks</h1>
    <p className="text-gray-600">All tasks view coming soon...</p>
  </div>
);

const AllHours = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <h1 className="text-2xl font-bold text-gray-900 mb-4">All Hours</h1>
    <p className="text-gray-600">All hours view coming soon...</p>
  </div>
);

const Reminders = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <h1 className="text-2xl font-bold text-gray-900 mb-4">Reminders</h1>
    <p className="text-gray-600">Reminders view coming soon...</p>
  </div>
);

// Root Redirect - redirects based on user role
const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect suppliers to QC incidents, others to dashboard
  if (isSupplier(user)) {
    return <Navigate to="/qc-incidents" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <DevModeProvider>
          <AuthProvider>
          <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/supplier-login" 
            element={
              <PublicRoute>
                <SupplierLogin />
              </PublicRoute>
            } 
          />

          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-projects" 
            element={
              <ProtectedRoute>
                <MyProjects />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-tasks" 
            element={
              <ProtectedRoute>
                <MyTasks />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/projects" 
            element={
              <ProtectedRoute>
                <AllProjects />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-hours" 
            element={
              <ProtectedRoute>
                <MyHours />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tasks" 
            element={
              <ProtectedRoute>
                <AllTasks />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hours" 
            element={
              <ProtectedRoute>
                <AllHours />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/reminders" 
            element={
              <ProtectedRoute>
                <Reminders />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/airports" 
            element={
              <ProtectedRoute>
                <AirportsAdmin />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/airports/pm-view" 
            element={
              <ProtectedRoute>
                <PMView />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/chart-test" 
            element={
              <ProtectedRoute>
                <ChartTest />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/simple-charts" 
            element={
              <ProtectedRoute>
                <SimpleCharts />
              </ProtectedRoute>
            } 
          />

          {/* QC Incidents Routes */}
          <Route 
            path="/qc-incidents" 
            element={
              <ProtectedRoute allowSuppliers={true}>
                <QCIncidents />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/qc-incidents/new" 
            element={
              <ProtectedRoute allowSuppliers={true}>
                <QCIncidentForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/qc-incidents/:id" 
            element={
              <ProtectedRoute allowSuppliers={true}>
                <QCIncidentDetail />
              </ProtectedRoute>
            } 
          />

          {/* Global Settings Route */}
          <Route 
            path="/admin/settings" 
            element={
              <ProtectedRoute>
                <GlobalSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/qc-import" 
            element={
              <ProtectedRoute>
                <DataMigration />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/finance" 
            element={
              <ProtectedRoute>
                <FinanceAdmin />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/business-dev" 
            element={
              <ProtectedRoute>
                <BusinessDevAdmin />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/pmo" 
            element={
              <ProtectedRoute>
                <PMODashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute allowSuppliers={true}>
                <UserProfile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/central" 
            element={
              <ProtectedRoute>
                <CentralDashboard />
              </ProtectedRoute>
            } 
          />

          {/* Redirect root based on user role */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* 404 redirect based on user role */}
          <Route path="*" element={<RootRedirect />} />
        </Routes>
          </AuthProvider>
        </DevModeProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
